import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import recordingSegmentTransferFailed, {
	type RecordingSegmentTransferFailedAction,
} from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentTransferFailed';
import recordingSegmentTransferred, {
	type RecordingSegmentTransferredAction,
} from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentTransferred';
import type TransferRecordingSegment from '@Application/Beneficiaries/Interviews/Commands/TransferRecordingSegment';
import { putFormData } from '@Domain/Api';
import ApiError from '@Infra/FetchLib/ApiError';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import type * as React from 'react';

/**
 * Response shape of the segment transfer endpoint. `id`, `facility` and `storedAt` are present but
 * ignored; `position` is renamed to `segment` by the caller (`createRecordingTransferHandler`),
 * never here — this type mirrors the wire shape as-is.
 */
export type RecordingSegmentPayload = {
	readonly id: number;
	readonly facility: { readonly id: number };
	readonly position: number;
	readonly digest: string;
	readonly byteSize: number;
	readonly storedAt: string;
};

type Actions = RecordingSegmentTransferredAction | RecordingSegmentTransferFailedAction;

/** Dispatched by the transfer handler through its own `dispatch`: R5 transfers one recording segment. */
const transferRecordingSegment = (command: TransferRecordingSegment) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const payload = await putFormData<RecordingSegmentPayload>(
				`${buildSubjectApiPath(command.facilityId, command.subject)}/interviews/${command.interviewId}/recordings/${command.recordingId}/segments/${command.position}`,
				command.toFormData(),
			);

			const action = recordingSegmentTransferred(
				payload,
				command.interviewId,
				command.recordingId,
				command.subject.typeKey,
				command.subject.subjectId,
			);
			dispatch(action);
			return action;
		} catch (error) {
			const action = recordingSegmentTransferFailed(
				extractMessages(error),
				error instanceof ApiError ? error.status : null,
			);
			dispatch(action);
			return Promise.reject(action);
		}
	};
};

export default transferRecordingSegment;
